import React, { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import Text from "@components/Text";
import { Green, Gray } from "@components/Dot";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { isDeviceOnline } from "@src/utils/deviceOnline";
import { detachDevice } from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";
import { toast } from "react-toastify";
import AttachDeviceDialog from "./AttachDeviceDialog";

const Table = styled.div`
  width: 100%;
  overflow-x: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0 -16px;
    padding: 0 16px;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 1.5fr 1.5fr 1fr 1fr 1fr 80px;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeeef;
  min-width: 600px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1.5fr 1.5fr 1fr 1fr 1fr 80px;
  padding: 14px 16px;
  border-bottom: 1px solid #f5f5f5;
  align-items: center;
  min-width: 600px;
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;

const DotCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EsnText = styled.span`
  font-family: monospace;
  font-size: 13px;
  color: #4a90d9;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const Badge = styled.span`
  display: inline-block;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
  background: #f0f1f3;
  border-radius: 10px;
  padding: 2px 8px;
  margin-left: 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
`;

const AttachBtn = styled.button`
  cursor: pointer;
  outline: none;
  height: 32px;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 13px;
  background: #68ad18;
  color: #fff;
  border: none;
  padding: 0 16px;
  white-space: nowrap;
  &:hover {
    box-shadow: inset 0px 0px 5px rgba(0, 0, 0, 0.3);
  }
`;

const DetachBtn = styled.button`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #e74c3c;
  background: none;
  border: 1px solid #e74c3c;
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  &:hover {
    background: #fdf0ee;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const formatDate = (iso: string | null) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

interface Props {
  userId: string;
  devices: Array<{
    attachmentId: string;
    esn: string;
    name: string;
    lastConnectedAt: string | null;
    createdAt: string;
    detachedAt: string | null;
  }>;
  /** Called after an attach or detach so the parent can reload the user. */
  onChanged?: () => void;
}

const UserDevicesSubTab: React.FC<Props> = ({ userId, devices, onChanged }) => {
  const navigate = useNavigate();
  const [detachingId, setDetachingId] = useState<string | null>(null);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);

  const activeDevices = devices.filter((d) => !d.detachedAt);
  const detachedDevices = devices.filter((d) => d.detachedAt);

  const handleDetach = async (attachmentId: string, esn: string) => {
    if (!window.confirm(`Detach device ${esn} from this user?`)) return;
    setDetachingId(attachmentId);
    try {
      await detachDevice(attachmentId);
      toast.success(`Device ${esn} detached`);
      onChanged?.();
    } catch {
      // error toast is shown by api client
    } finally {
      setDetachingId(null);
    }
  };

  const attachDialog = (
    <AttachDeviceDialog
      visible={attachDialogOpen}
      userId={userId}
      onClose={() => setAttachDialogOpen(false)}
      onAttached={onChanged}
    />
  );

  if (devices.length === 0) {
    return (
      <div>
        <HeaderRow>
          <span />
          <AttachBtn onClick={() => setAttachDialogOpen(true)}>
            Attach device
          </AttachBtn>
        </HeaderRow>
        <EmptyState>
          <Text size={14} color="#899093">
            This user has no devices.
          </Text>
        </EmptyState>
        {attachDialog}
      </div>
    );
  }

  return (
    <div>
      <HeaderRow>
        <Text size={13} color="#899093">
          Active devices <Badge>{activeDevices.length}</Badge>
          {detachedDevices.length > 0 && (
            <span style={{ marginLeft: 16 }}>
              Detached <Badge>{detachedDevices.length}</Badge>
            </span>
          )}
        </Text>
        <AttachBtn onClick={() => setAttachDialogOpen(true)}>
          Attach device
        </AttachBtn>
      </HeaderRow>

      <Table>
        <TableHeader>
          <span />
          <Text size={12} color="#899093" type="heading">
            ESN
          </Text>
          <Text size={12} color="#899093" type="heading">
            NAME
          </Text>
          <Text size={12} color="#899093" type="heading">
            LAST CONNECTED
          </Text>
          <Text size={12} color="#899093" type="heading">
            ATTACHED
          </Text>
          <Text size={12} color="#899093" type="heading">
            DETACHED
          </Text>
          <span />
        </TableHeader>

        {activeDevices.map((device) => (
          <TableRow
            key={device.attachmentId}
            onClick={() => navigate(`${ROUTES.ADMIN}/devices/${device.esn}`)}
          >
            <DotCell>
              {isDeviceOnline(device.lastConnectedAt) ? <Green /> : <Gray />}
            </DotCell>
            <EsnText>{device.esn}</EsnText>
            <Text size={13} color="#494C5B">
              {device.name}
            </Text>
            <Text size={13} color="#494C5B">
              {formatDate(device.lastConnectedAt)}
            </Text>
            <Text size={13} color="#494C5B">
              {formatDate(device.createdAt)}
            </Text>
            <Text size={13} color="#899093">
              -
            </Text>
            <DetachBtn
              disabled={detachingId === device.attachmentId}
              onClick={(e) => {
                e.stopPropagation();
                handleDetach(device.attachmentId, device.esn);
              }}
            >
              {detachingId === device.attachmentId ? "..." : "Detach"}
            </DetachBtn>
          </TableRow>
        ))}

        {detachedDevices.map((device) => (
          <TableRow
            key={device.attachmentId}
            style={{ opacity: 0.6 }}
            onClick={() => navigate(`${ROUTES.ADMIN}/devices/${device.esn}`)}
          >
            <DotCell>
              <Gray />
            </DotCell>
            <EsnText style={{ color: "#899093" }}>{device.esn}</EsnText>
            <Text size={13} color="#899093">
              {device.name}
            </Text>
            <Text size={13} color="#899093">
              {formatDate(device.lastConnectedAt)}
            </Text>
            <Text size={13} color="#899093">
              {formatDate(device.createdAt)}
            </Text>
            <Text size={13} color="#e74c3c">
              {formatDate(device.detachedAt)}
            </Text>
            <span />
          </TableRow>
        ))}
      </Table>
      {attachDialog}
    </div>
  );
};

export default UserDevicesSubTab;
